import React, { type FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { t } from 'i18next';

import { COLORS } from 'Components/foundation';
import { useTypedSelector } from 'Libs/redux/utils';
import STextArea from 'Components/structural/STextArea/STextArea';
import UMediaPicker from 'Components/unit/UMediaPicker/UMediaPicker';
import SInput from 'Components/structural/SInput/SInput';
import { SDivider } from 'Components/structural';
import UIcon from 'Components/unit/UIcon/UIcon';
import { UFieldError } from 'Components/unit/UFieldError/UFieldError';
import STooltip from 'Components/structural/STooltip/STooltip';
import { CampaignFormValues } from 'Pages/Activity/Campaign/saveCampaign.hook';

import styles from './Overview.style';
import { OverviewValidation } from '../../OverviewValidation/OverviewValidation';

const TOOLTIP_ID = 'disclaimer-info';
const MAX_TITLE_LENGTH = 40;
const MAX_DESCRIPTION_LENGTH = 120;
const MAX_DISCLAIMER_LENGTH = 10000;

export const Overview: FC = () => {
  const { selectedLanguage } = useTypedSelector((state) => state.entities.activity.edition);

  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CampaignFormValues>();

  return (
    <>
      <div style={styles.container}>
        <section style={styles.section}>
          <OverviewValidation />
        </section>
      </div>

      <div style={styles.container}>
        <section style={styles.section}>
          <div style={styles.introBlock}>
            <div style={styles.campaignExplanationBlock}>
              <div style={styles.title}>{t('activities:overview.form.title')}</div>
              <div style={styles.comment}>{t('activities:overview.form.description')}</div>
            </div>
            <div style={styles.imagePickerBlock}>
              <UMediaPicker
                dropArea
                image={watch('coverImage')?.url || undefined}
                video={undefined}
                media={{ mediaType: 'image', onChange: (value) => setValue('coverImage', value) }}
                onClearMedia={() => setValue('coverImage', undefined)}
                size="L"
                isLabelRequired
                label={t('activities:overview.form.choose_cover_image')}
              />
              <UFieldError errorMessage={errors?.coverImage?.message} />
            </div>
          </div>
          <div style={styles.inputBlock}>
            <SInput
              type="large"
              required
              maxLength={MAX_TITLE_LENGTH}
              value={watch(`name.${selectedLanguage}`)}
              label={t('activities:overview.form.name_input_title')}
              placeholder={t('activities:overview.form.name_input_placeholder')}
              onChange={(value) => setValue(`name.${selectedLanguage}`, value)}
              errorMessage={errors?.name?.message}
            />
          </div>
          <div style={styles.inputBlock}>
            <STextArea
              isLabelRequired
              label={t('activities:overview.form.description_input_title')}
              placeholder={t('activities:overview.form.description_input_placeholder')}
              height="100"
              maxLength={MAX_DESCRIPTION_LENGTH}
              value={watch(`description.${selectedLanguage}`)}
              onChange={(value) => setValue(`description.${selectedLanguage}`, value)}
              errorMessage={errors?.description?.message}
            />
          </div>
          <SDivider text={t('activities:overview.form.advanced_options')} />
          <div style={styles.inputBlock}>
            <div style={{ ...styles.subTitle, display: 'flex' }}>
              {t('activities:overview.form.campaign_disclaimer_title')}
              <div data-tooltip-id={TOOLTIP_ID} style={styles.disclaimerBlock}>
                <UIcon name="info" size={13} color={COLORS.TEXT.PLACEHOLDER_DEFAULT} />
                <STooltip
                  id={TOOLTIP_ID}
                  position="top"
                  content={t('activities:overview.form.campaign_disclaimer_tooltip')}
                />
              </div>
            </div>
            <STextArea
              height="153"
              placeholder={t('activities:overview.form.campaign_disclaimer_placeholder')}
              maxLength={MAX_DISCLAIMER_LENGTH}
              value={watch(`disclaimer.${selectedLanguage}`)}
              onChange={(value) => setValue(`disclaimer.${selectedLanguage}`, value)}
              errorMessage={errors?.disclaimer?.message}
            />
          </div>
        </section>
        <div style={styles.requiredFields}>{t('activities:overview.form.required_fields')}</div>
      </div>
    </>
  );
};
